import { DFPModal } from "@/components/modals/dfp-modal";
import { auth } from "@/services/auth-service";
import { useUserStore } from "@/stores/user-store";
import { Button, Checkbox, Form, Input, Spin } from "antd";
import { useEffect, useReducer } from "react";
import "./SyncModal.scss";
import { CORE_URL } from "@/constants/api";
import { VITE_CORE_API_URL } from "@/constants/env";

interface Location {
	id: string;
	address: string;
	store_number?: string;
}

interface SyncModalProps {
	isOpen: boolean;
	toggle: () => void;
	organizationName: string;
	locations: Location[];
	onSync: (
		selectedLocations: string[],
		integrationId: string,
		serviceChannel: boolean,
		ecotrak: boolean,
		assetsQueued: number,
		photoSync?: boolean,
	) => void;
	loading?: boolean;
}

interface State {
	serviceChannel: boolean;
	ecotrak: boolean;
	photoSync: boolean;
	forceOverride: boolean;
	selectedLocations: string[];
	selectAll: boolean;
	locationIdsInput: string;
	locationIdsError: string;
	searchTerm: string;
	syncError: string;
}

type Action =
	| { type: "RESET" }
	| { type: "SET_SERVICE_CHANNEL"; payload: boolean }
	| { type: "SET_ECOTRAK"; payload: boolean }
	| { type: "SET_PHOTO_SYNC"; payload: boolean }
	| { type: "SET_FORCE_OVERRIDE"; payload: boolean }
	| { type: "TOGGLE_SELECT_ALL"; payload: Location[] }
	| { type: "TOGGLE_LOCATION"; payload: string }
	| { type: "SET_SELECTED_LOCATIONS"; payload: string[] }
	| { type: "SET_LOCATION_IDS_INPUT"; payload: string }
	| { type: "SET_LOCATION_IDS_ERROR"; payload: string }
	| { type: "SET_SEARCH_TERM"; payload: string }
	| { type: "SET_SYNC_ERROR"; payload: string };

const initialState: State = {
	serviceChannel: false,
	ecotrak: false,
	photoSync: false,
	forceOverride: false,
	selectedLocations: [],
	selectAll: false,
	locationIdsInput: "",
	locationIdsError: "",
	searchTerm: "",
	syncError: "",
};

function reducer(state: State, action: Action): State {
	switch (action.type) {
		case "RESET":
			return initialState;

		case "SET_SERVICE_CHANNEL":
			return {
				...state,
				serviceChannel: action.payload,
				ecotrak: action.payload ? false : state.ecotrak,
			};

		case "SET_ECOTRAK":
			return {
				...state,
				ecotrak: action.payload,
				serviceChannel: action.payload ? false : state.serviceChannel,
			};

		case "SET_PHOTO_SYNC":
			return {
				...state,
				photoSync: action.payload,
			};

		case "SET_FORCE_OVERRIDE":
			return {
				...state,
				forceOverride: action.payload,
			};

		case "TOGGLE_SELECT_ALL":
			const newSelectAll = !state.selectAll;
			return {
				...state,
				selectAll: newSelectAll,
				selectedLocations: newSelectAll
					? action.payload.map((loc) => loc.id)
					: [],
				locationIdsInput: "",
				locationIdsError: "",
			};

		case "TOGGLE_LOCATION":
			const newSelectedLocations = state.selectedLocations.includes(
				action.payload,
			)
				? state.selectedLocations.filter((id) => id !== action.payload)
				: [...state.selectedLocations, action.payload];

			return {
				...state,
				selectedLocations: newSelectedLocations,
				selectAll: false,
				locationIdsInput: "",
				locationIdsError: "",
			};

		case "SET_SELECTED_LOCATIONS":
			return {
				...state,
				selectedLocations: action.payload,
				selectAll: false,
			};

		case "SET_LOCATION_IDS_INPUT":
			return {
				...state,
				locationIdsInput: action.payload,
			};

		case "SET_LOCATION_IDS_ERROR":
			return {
				...state,
				locationIdsError: action.payload,
			};

		case "SET_SEARCH_TERM":
			return {
				...state,
				searchTerm: action.payload,
			};

		case "SET_SYNC_ERROR":
			return {
				...state,
				syncError: action.payload,
			};

		default:
			return state;
	}
}

export const SyncModal = ({
	isOpen,
	toggle,
	organizationName,
	locations,
	onSync,
	loading = false,
}: SyncModalProps) => {
	const [state, dispatch] = useReducer(reducer, initialState);
	const user = useUserStore((state) => state.user);

	useEffect(() => {
		if (isOpen) {
			dispatch({ type: "RESET" });
		}
	}, [isOpen]);

	const handleServiceChannelChange = (e: any) => {
		dispatch({ type: "SET_SERVICE_CHANNEL", payload: e.target.checked });
	};

	const handleEcotrakChange = (e: any) => {
		dispatch({ type: "SET_ECOTRAK", payload: e.target.checked });
	};

	const handlePhotoSyncChange = (e: any) => {
		dispatch({ type: "SET_PHOTO_SYNC", payload: e.target.checked });
	};

	const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		dispatch({ type: "SET_SEARCH_TERM", payload: e.target.value });
	};

	// Filter locations based on search term (matches address or store number)
	const filteredLocations = locations.filter((location) => {
		if (!state.searchTerm.trim()) return true;
		const searchLower = state.searchTerm.toLowerCase();
		const addressMatch = location.address.toLowerCase().includes(searchLower);
		const storeNumberMatch = location.store_number
			?.toLowerCase()
			.includes(searchLower);
		return addressMatch || storeNumberMatch;
	});

	// Compute if all filtered locations are currently selected
	const allFilteredSelected =
		filteredLocations.length > 0 &&
		filteredLocations.every((loc) => state.selectedLocations.includes(loc.id));

	const handleSelectAll = () => {
		if (allFilteredSelected) {
			// Deselect all filtered locations
			const filteredIds = new Set(filteredLocations.map((loc) => loc.id));
			const remaining = state.selectedLocations.filter(
				(id) => !filteredIds.has(id),
			);
			dispatch({ type: "SET_SELECTED_LOCATIONS", payload: remaining });
		} else {
			// Select all filtered locations (merge with existing selections)
			const newSelections = new Set([
				...state.selectedLocations,
				...filteredLocations.map((loc) => loc.id),
			]);
			dispatch({
				type: "SET_SELECTED_LOCATIONS",
				payload: Array.from(newSelections),
			});
		}
	};

	const handleLocationSelect = (locationId: string) => {
		dispatch({ type: "TOGGLE_LOCATION", payload: locationId });
	};

	const handleLocationIdsInputChange = (
		e: React.ChangeEvent<HTMLTextAreaElement>,
	) => {
		const value = e.target.value;
		dispatch({ type: "SET_LOCATION_IDS_INPUT", payload: value });

		// Clear previous errors
		dispatch({ type: "SET_LOCATION_IDS_ERROR", payload: "" });

		if (value.trim()) {
			// Parse the input
			const inputIds = value
				.split(/[,\n\r\s]+/)
				.map((id) => id.trim())
				.filter((id) => id.length > 0);

			// Validate that all IDs exist in the locations list
			const validLocationIds = locations.map((loc) => loc.id);
			const invalidIds = inputIds.filter(
				(id) => !validLocationIds.includes(id),
			);

			if (invalidIds.length > 0) {
				dispatch({
					type: "SET_LOCATION_IDS_ERROR",
					payload: `Invalid location IDs: ${invalidIds.join(", ")}`,
				});
			} else if (inputIds.length > 0) {
				// Valid IDs - update selected locations
				dispatch({ type: "SET_SELECTED_LOCATIONS", payload: inputIds });
			}
		} else {
			// Clear selections when input is empty
			dispatch({ type: "SET_SELECTED_LOCATIONS", payload: [] });
		}
	};

	const clearLocationInput = () => {
		dispatch({ type: "SET_LOCATION_IDS_INPUT", payload: "" });
		dispatch({ type: "SET_LOCATION_IDS_ERROR", payload: "" });
		dispatch({ type: "SET_SELECTED_LOCATIONS", payload: [] });
	};

	const handleSubmit = async () => {
		// Clear any previous errors
		dispatch({ type: "SET_SYNC_ERROR", payload: "" });

		try {
			const token = await auth?.currentUser?.getIdToken();
			if (!token) {
				throw new Error("No authentication token available");
			}

			let assetsQueued = 0;

			// Handle photo sync if selected
			if (state.photoSync) {
				const photoParams = new URLSearchParams();
				if (state.selectedLocations.length > 0) photoParams.set("locationIds", state.selectedLocations.join(","));
				if (state.forceOverride) photoParams.set("forceOverride", "true");
				const photoQueryString = photoParams.toString() ? `?${photoParams.toString()}` : "";
				const photoSyncResponse = await fetch(
					`${VITE_CORE_API_URL}${CORE_URL}/outside-source/integration/photo-sync${photoQueryString}`,
					{
						method: "POST",
						headers: {
							"Content-Type": "application/json",
							Authorization: `Bearer ${token}`,
						},
					},
				);

				if (!photoSyncResponse.ok) {
					throw new Error("Photo sync failed");
				}

				const photoSyncData = await photoSyncResponse.json();
				// Extract number of assets from photo sync message
				const photoAssetsMatch = photoSyncData.message.match(
					/queued for (\d+) assets/,
				);
				if (photoAssetsMatch) {
					assetsQueued += parseInt(photoAssetsMatch[1], 10);
				}
			}

			// Handle regular asset sync if selected
			if (state.serviceChannel || state.ecotrak) {
				// Check if locations are selected for regular sync
				if (state.selectedLocations.length === 0) {
					throw new Error("Please select at least one location for asset sync");
				}

				// Check if all selected locations have store numbers
				const selectedLocationsData = locations.filter((loc) =>
					state.selectedLocations.includes(loc.id),
				);
				const locationsWithoutStoreNumber = selectedLocationsData.filter(
					(loc) => !loc.store_number || loc.store_number.trim() === "",
				);

				if (locationsWithoutStoreNumber.length > 0) {
					const locationAddresses = locationsWithoutStoreNumber
						.map((loc) => loc.address)
						.join(", ");
					throw new Error(
						`Please add a store number to the following location(s) before syncing: ${locationAddresses}`,
					);
				}

				const integrationId = state.serviceChannel
					? "1"
					: state.ecotrak
						? "2"
						: "";
				const locationIds = state.selectedLocations.join(",");
				const overrideParam = state.forceOverride ? "&forceOverride=true" : "";

				const response = await fetch(
					`${VITE_CORE_API_URL}${CORE_URL}/outside-source/integration/sync?integrationId=${integrationId}&locationIds=${locationIds}${overrideParam}`,
					{
						method: "POST",
						headers: {
							"Content-Type": "application/json",
							Authorization: `Bearer ${token}`,
						},
					},
				);

				if (!response.ok) {
					throw new Error("Asset sync failed");
				}

				const data = await response.json();
				// Extract the number of assets from the message
				const assetsMatch = data.message.match(/queued for (\d+) assets/);
				if (assetsMatch) {
					assetsQueued += parseInt(assetsMatch[1], 10);
				}
			}

			onSync(
				state.selectedLocations,
				state.serviceChannel ? "1" : state.ecotrak ? "2" : "",
				state.serviceChannel,
				state.ecotrak,
				assetsQueued,
				state.photoSync,
			);
			toggle();
		} catch (error) {
			console.error("Error syncing:", error);
			const errorMessage =
				error instanceof Error
					? error.message
					: "An error occurred during sync";
			dispatch({ type: "SET_SYNC_ERROR", payload: errorMessage });
		}
	};

	return (
		<DFPModal
			open={isOpen}
			title="Sync Assets"
			onCancel={toggle}
			onOk={handleSubmit}
			confirmLoading={loading}
			okButtonProps={{
				disabled: !state.serviceChannel && !state.ecotrak && !state.photoSync,
			}}
			width={600}
			bodyStyle={{
				maxHeight: "80vh",
				overflow: "hidden",
				display: "flex",
				flexDirection: "column",
				padding: "16px 24px",
			}}
			style={{
				top: 20,
			}}
		>
			<Form
				layout="vertical"
				style={{
					height: "100%",
					display: "flex",
					flexDirection: "column",
					flex: 1,
					minHeight: 0,
				}}
			>
				{state.syncError && (
					<div
						style={{
							backgroundColor: "#fff2f0",
							border: "1px solid #ffccc7",
							borderRadius: "6px",
							padding: "12px 16px",
							marginBottom: "16px",
							color: "#ff4d4f",
							fontSize: "14px",
							display: "flex",
							alignItems: "flex-start",
							gap: "8px",
						}}
					>
						<span style={{ fontSize: "16px" }}>⚠️</span>
						<span style={{ flex: 1 }}>{state.syncError}</span>
					</div>
				)}
				<div
					className="sync-provider-label"
					style={{ fontWeight: 500, marginBottom: 8 }}
				>
					Service Provider To Sync
				</div>
				<div className="sync-options">
					<Checkbox
						checked={state.serviceChannel}
						onChange={handleServiceChannelChange}
					>
						Service Channel
					</Checkbox>
					<Checkbox checked={state.ecotrak} onChange={handleEcotrakChange}>
						Ecotrak
					</Checkbox>
				</div>

				<div
					className="sync-provider-label"
					style={{ fontWeight: 500, marginBottom: 4, marginTop: 8 }}
				>
					Photo Sync
				</div>
				<div className="sync-options" style={{ marginBottom: 8 }}>
					<Checkbox checked={state.photoSync} onChange={handlePhotoSyncChange}>
						Sync Asset Photos to Service Channel
					</Checkbox>
				</div>

				<div
					className="sync-provider-label"
					style={{ fontWeight: 500, marginBottom: 4, marginTop: 8 }}
				>
					Override
				</div>
				<div className="sync-options" style={{ marginBottom: 8 }}>
					<Checkbox
						checked={state.forceOverride}
						onChange={(e: any) => dispatch({ type: "SET_FORCE_OVERRIDE", payload: e.target.checked })}
					>
						Force push all FAI data (skip freshness check)
					</Checkbox>
				</div>

				<div
					className="locations-section"
					style={{
						flex: 1,
						minHeight: 200,
						display: "flex",
						flexDirection: "column",
						opacity: state.serviceChannel || state.ecotrak ? 1 : 0.5,
						marginTop: 16,
					}}
				>
					<div
						className="locations-title"
						style={{ marginBottom: 12, fontWeight: 500 }}
					>
						Locations To Sync{" "}
						{!state.serviceChannel &&
							!state.ecotrak &&
							"(Not required for photo sync)"}
					</div>

					{/* Location IDs Input Section */}
					<div
						className="location-ids-input-section"
						style={{ marginBottom: 16, flexShrink: 0 }}
					>
						<div style={{ marginBottom: 8, fontWeight: 500, fontSize: 14 }}>
							Quick Select by Location IDs
						</div>
						<Input.TextArea
							placeholder="Enter location IDs separated by commas, spaces, or new lines&#10;Example: loc123, loc456, loc789"
							value={state.locationIdsInput}
							onChange={handleLocationIdsInputChange}
							disabled={!state.serviceChannel && !state.ecotrak}
							rows={3}
							style={{
								marginBottom: 4,
								fontFamily: "monospace",
								fontSize: 12,
							}}
						/>
						{state.locationIdsError && (
							<div style={{ color: "#ff4d4f", fontSize: 12, marginBottom: 8 }}>
								{state.locationIdsError}
							</div>
						)}
						{state.locationIdsInput && !state.locationIdsError && (
							<div style={{ fontSize: 12, color: "#52c41a", marginBottom: 8 }}>
								✓ {state.selectedLocations.length} location(s) selected
							</div>
						)}
						{state.locationIdsInput && (
							<Button
								size="small"
								onClick={clearLocationInput}
								style={{ marginBottom: 8 }}
							>
								Clear Input
							</Button>
						)}
					</div>

					<div
						className="locations-container"
						style={{
							flex: 1,
							minHeight: 150,
							display: "flex",
							flexDirection: "column",
							border: "1px solid #d9d9d9",
							borderRadius: "6px",
							backgroundColor: "#fafafa",
						}}
					>
						{/* Search Input */}
						<div
							style={{
								padding: "8px 12px",
								borderBottom: "1px solid #d9d9d9",
								flexShrink: 0,
							}}
						>
							<Input
								placeholder="Search by address or store number..."
								value={state.searchTerm}
								onChange={handleSearchChange}
								disabled={!state.serviceChannel && !state.ecotrak}
								allowClear
								style={{ marginBottom: 8 }}
							/>
							<Checkbox
								checked={allFilteredSelected}
								onChange={handleSelectAll}
								disabled={
									(!state.serviceChannel && !state.ecotrak) ||
									filteredLocations.length === 0
								}
							>
								<span className="select-all-label">
									Select All
									{state.searchTerm
										? ` (${filteredLocations.length} matching)`
										: ` (${locations.length})`}
								</span>
							</Checkbox>
						</div>
						<div
							className="location-list"
							style={{
								flex: 1,
								overflowY: "auto",
								padding: "8px 12px",
								maxHeight: "300px",
							}}
						>
							{loading ? (
								<div
									style={{
										display: "flex",
										justifyContent: "center",
										alignItems: "center",
										height: "100px",
									}}
								>
									<Spin size="large" />
								</div>
							) : filteredLocations.length === 0 ? (
								<div
									style={{
										display: "flex",
										justifyContent: "center",
										alignItems: "center",
										height: "100px",
										color: "#999",
									}}
								>
									No locations match your search
								</div>
							) : (
								filteredLocations.map((location) => (
									<div
										key={location.id}
										className="location-item"
										style={{ marginBottom: "8px" }}
									>
										<Checkbox
											checked={state.selectedLocations.includes(location.id)}
											onChange={() => handleLocationSelect(location.id)}
											disabled={!state.serviceChannel && !state.ecotrak}
										>
											<span
												title={`ID: ${location.id}`}
												style={{ display: "flex", flexDirection: "column" }}
											>
												<span>
													{location.store_number && (
														<span
															style={{
																fontWeight: 600,
																color: "#1890ff",
																marginRight: 8,
															}}
														>
															#{location.store_number}
														</span>
													)}
													{location.address}
												</span>
											</span>
										</Checkbox>
									</div>
								))
							)}
						</div>
					</div>
				</div>
			</Form>
		</DFPModal>
	);
};
